/**
 * cmrShared.jsx
 * —────────────────────────────────────────────────────────────
 * Átomos, moléculas y organismo exclusivos del módulo CMR.
 *
 * Constantes de UI (no vienen de API):
 *   DIAS_SEMANA
 *
 * Átomos:
 *   CmrFieldLabel etiqueta con asterisco y tooltip
 *   CmrHelpText texto de ayuda bajo un campo
 *   CmrInfoBanner banner informativo (PrimeReact Message)
 *   CmrInfoRow fila de lectura con borde
 *   CmrCardTitle cabecera de sección con ícono
 *   CmrDropdownSkeleton spinner mientras carga un catálogo
 *   CmrFieldError error inline bajo un campo
 *
 * Moléculas:
 *   CmrDropdownField campo completo: label + loading/dropdown + error + helpText
 *   CmrDaySelector selector de días de la semana
 *
 * Organismo:
 *   CmrStepsBar barra de progreso del wizard
 *
 * Helpers:
 *   getMod obtiene modificación pendiente o valor original
 */

import { memo }      from "react";
import { Button }    from "primereact/button";
import { Dropdown }  from "primereact/dropdown";
import { Message }   from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";

// ─── CONSTANTES ───────────────────────────────────────────────────────────────

export const DIAS_SEMANA = [
    { key: "LUNES", label: "Lun" },
    { key: "MARTES", label: "Mar" },
    { key: "MIERCOLES", label: "Mié" },
    { key: "JUEVES", label: "Jue" },
    { key: "VIERNES", label: "Vie" },
    { key: "SABADO", label: "Sáb" },
    { key: "DOMINGO", label: "Dom" },
];

// ─── HELPER ───────────────────────────────────────────────────────────────────

/**
 * Devuelve el valor modificado de un campo si existe, o el valor original.
 * Función pura — no usa hooks.
 */
export const getMod = (modificaciones, estructuraId, field, defaultVal) =>
    modificaciones?.[estructuraId]?.[field] ?? defaultVal;

/** Valida que el correo de notificación tenga formato usuario@dominio.ext */
export const esCorreoValido = (correo) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((correo ?? "").trim());

/** Mensaje de error del correo solo cuando ya se escribió algo inválido (vacío no muestra error) */
export const errorCorreo = (correo) =>
    (correo ?? "").trim() && !esCorreoValido(correo) ? "Ingrese un correo válido (ejemplo@correo.com)" : null;

// ─── ÁTOMOS ──────────────────────────────────────────────────────────────────

/** Etiqueta de campo con asterisco requerido y tooltip opcional */
export const CmrFieldLabel = memo(({ children, required, tooltip }) => (
    <div className="cmr-label flex align-items-center gap-1 mb-1">
        {children}
        {required && <span className="cmr-label__required" aria-hidden="true">*</span>}
        {tooltip  && (
            <span title={tooltip} className="cmr-label__tooltip" aria-label={tooltip}>
                <i className="pi pi-info-circle" />
            </span>
        )}
    </div>
));
CmrFieldLabel.displayName = "CmrFieldLabel";

/** Texto de ayuda bajo un campo de formulario */
export const CmrHelpText = memo(({ icon = "pi-lightbulb", children }) => (
    <div className="cmr-help-text flex align-items-center gap-1 mt-1">
        <i className={`pi ${icon}`} aria-hidden="true" />
        {children}
    </div>
));
CmrHelpText.displayName = "CmrHelpText";

/** Banner informativo usando PrimeReact Message */
export const CmrInfoBanner = memo(({ severity = "info", children }) => (
    <Message
        severity={severity}
        className="w-full mb-3 justify-content-start"
        content={
            <div className="flex align-items-start gap-2 text-sm">
                <div>{children}</div>
            </div>
        }
    />
));
CmrInfoBanner.displayName = "CmrInfoBanner";

/** Fila de datos de solo lectura con fondo neutro */
export const CmrInfoRow = memo(({ children }) => (
    <div className="cmr-info-row flex align-items-center gap-2 p-2 px-3">
        {children}
    </div>
));
CmrInfoRow.displayName = "CmrInfoRow";

/** Cabecera de sección con ícono y subtítulo opcional */
export const CmrCardTitle = memo(({ icon, title, subtitle }) => (
    <div className="cmr-card-title flex align-items-center gap-3 pb-3 mb-4">
        <div className="cmr-card-icon flex align-items-center justify-content-center flex-shrink-0" aria-hidden="true">
            <i className={`pi ${icon}`} />
        </div>
        <div>
            <p className="cmr-card-title__text">{title}</p>
            {subtitle && <p className="cmr-card-title__sub">{subtitle}</p>}
        </div>
    </div>
));
CmrCardTitle.displayName = "CmrCardTitle";

/** Placeholder mientras el catálogo de un dropdown está cargando */
export const CmrDropdownSkeleton = memo(({ label }) => (
    <div className="cmr-dropdown-skeleton p-inputtext w-full flex align-items-center gap-2">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
        <span className="text-color-secondary text-sm">{label ?? "Cargando..."}</span>
    </div>
));
CmrDropdownSkeleton.displayName = "CmrDropdownSkeleton";

/** Mensaje de error inline bajo un campo */
export const CmrFieldError = memo(({ message }) =>
    message ? (
        <div className="cmr-field-error flex align-items-center gap-1 mt-1" role="alert">
            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            <span className="text-sm">{message}</span>
        </div>
    ) : null
);
CmrFieldError.displayName = "CmrFieldError";

// ─── MOLÉCULA: CmrDropdownField ───────────────────────────────────────────────

/**
 * Campo de dropdown completo: label + loading/skeleton + dropdown + error + helpText.
 *
 * Props:
 *   label        {string}   texto de la etiqueta
 *   required     {boolean}  muestra asterisco
 *   tooltip      {string}   texto del tooltip en la etiqueta
 *   helpText     {string}   texto de ayuda bajo el campo (opcional)
 *   helpIcon     {string}   ícono PrimeIcons para el helpText (sin "pi-")
 *   catalog      {object}   { data, loading, error } del useCmrCatalogs
 *   value        {*}        valor seleccionado
 *   onChange     {function} callback (e) => void
 *   optionLabel  {string}   campo del objeto a mostrar (default: "nombre")
 *   optionValue  {string}   campo del objeto como valor (default: "id")
 *   placeholder  {string}   texto cuando no hay selección
 *   disabled     {boolean}  deshabilita el control
 */
export const CmrDropdownField = memo(({
                                          label,
                                          required,
                                          tooltip,
                                          helpText,
                                          helpIcon,
                                          catalog,
                                          value,
                                          onChange,
                                          optionLabel  = "nombre",
                                          optionValue  = "id",
                                          placeholder  = "Selecciona...",
                                          disabled     = false,
                                          filter = false,
                                          emptyMessage = "Sin opciones disponibles",
                                          emptyFilterMessage = "No se encontraron coincidencias",
                                      }) => (
    <div className="cmr-field-group">
        <CmrFieldLabel required={required} tooltip={tooltip}>
            {label}
        </CmrFieldLabel>

        {catalog.loading ? (
            <CmrDropdownSkeleton label={`Cargando ${label.toLowerCase()}...`} />
        ) : (
            <Dropdown
                value={value}
                options={Array.isArray(catalog.data) ? catalog.data : []}
                optionLabel={optionLabel}
                optionValue={optionValue}
                placeholder={placeholder}
                onChange={onChange}
                filter={filter}
                disabled={disabled || catalog.loading}
                className="w-full"
                emptyMessage={emptyMessage}
                emptyFilterMessage={emptyFilterMessage}
            />
        )}

        <CmrFieldError message={catalog.error} />
        {helpText && <CmrHelpText icon={helpIcon}>{helpText}</CmrHelpText>}
    </div>
));
CmrDropdownField.displayName = "CmrDropdownField";

// ─── MOLÉCULA: CmrMultiSelectField ────────────────────────────────────────────

/**
 * Campo de multi-select completo: label + loading/skeleton + multiselect + error + helpText.
 *
 * Props:
 *   label           {string}    texto de la etiqueta
 *   required        {boolean}   muestra asterisco
 *   tooltip         {string}    texto del tooltip en la etiqueta
 *   helpText        {string}    texto de ayuda bajo el campo (opcional)
 *   helpIcon        {string}    ícono PrimeIcons para el helpText (sin "pi-")
 *   catalog         {object}    { data, loading, error } del useCmrCatalogs
 *   value           {Array}     valores seleccionados (array)
 *   onChange        {function}  callback (e) => void
 *   optionLabel     {string}    campo del objeto a mostrar (default: "nombre")
 *   optionValue     {string}    campo del objeto como valor (default: "id")
 *   placeholder     {string}    texto cuando no hay selección
 *   disabled        {boolean}   deshabilita el control
 *   filter          {boolean}   habilita búsqueda interna (default: false)
 *   maxSelectedLabels {number}  cuántos labels mostrar antes de colapsar (default: 3)
 *   selectedItemsLabel {string} texto cuando supera maxSelectedLabels (default: "{0} seleccionados")
 *   display         {string}    "comma" | "chip" — cómo renderiza los seleccionados (default: "comma")
 *   showSelectAll   {boolean}   muestra opción "Seleccionar todo" (default: true)
 */
export const CmrMultiSelectField = memo(({
                                             label,
                                             required,
                                             tooltip,
                                             helpText,
                                             helpIcon,
                                             catalog,
                                             value,
                                             onChange,
                                             optionLabel        = "nombre",
                                             optionValue        = "id",
                                             placeholder        = "Selecciona...",
                                             disabled           = false,
                                             filter             = false,
                                             maxSelectedLabels  = 3,
                                             selectedItemsLabel = "{0} seleccionados",
                                             display            = "comma",
                                             showSelectAll      = true,
                                         }) => (
    <div className="cmr-field-group">
        <CmrFieldLabel required={required} tooltip={tooltip}>
            {label}
        </CmrFieldLabel>

        {catalog.loading ? (
            <CmrDropdownSkeleton label={`Cargando ${label.toLowerCase()}...`} />
        ) : (
            <MultiSelect
                value={value}
                options={Array.isArray(catalog.data) ? catalog.data : []}
                optionLabel={optionLabel}
                optionValue={optionValue}
                placeholder={placeholder}
                onChange={onChange}
                filter={filter}
                disabled={disabled || catalog.loading}
                maxSelectedLabels={maxSelectedLabels}
                selectedItemsLabel={selectedItemsLabel}
                display={display}
                showSelectAll={showSelectAll}
                className="w-full"
            />
        )}

        <CmrFieldError message={catalog.error} />
        {helpText && <CmrHelpText icon={helpIcon}>{helpText}</CmrHelpText>}
    </div>
));

CmrMultiSelectField.displayName = "CmrMultiSelectField";

// ─── MOLÉCULA: CmrDaySelector ─────────────────────────────────────────────────

/**
 * Selector de días de la semana.
 * Props: selected {string[]}  ·  onChange {(days: string[]) => void}
 */
export const CmrDaySelector = memo(({ selected, onChange }) => {
    const toggle     = (day) => onChange(
        selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day]
    );
    const setLunVie  = () => onChange(["LUNES", "MARTES", "MIERCOLES", "JUEVES", "VIERNES"]);
    const setAllDays = () => onChange(DIAS_SEMANA.map((d) => d.key));

    return (
        <div>
            <div className="flex gap-2 mb-2">
                <Button label="Lun – Vie" icon="pi pi-calendar"      size="small" severity="secondary" outlined onClick={setLunVie}  />
                <Button label="Todos"     icon="pi pi-calendar-plus" size="small" severity="secondary" outlined onClick={setAllDays} />
            </div>
            <div className="flex gap-2 flex-wrap" role="group" aria-label="Días de operación">
                {DIAS_SEMANA.map((d) => (
                    <button
                        key={d.key}
                        type="button"
                        aria-pressed={selected.includes(d.key)}
                        className={`cmr-day-chip${selected.includes(d.key) ? " cmr-day-chip--selected" : ""}`}
                        onClick={() => toggle(d.key)}
                    >
                        {d.label}
                    </button>
                ))}
            </div>
        </div>
    );
});
CmrDaySelector.displayName = "CmrDaySelector";


export const CmrInputField = memo(({
                                       label,
                                       required,
                                       tooltip,
                                       helpText,
                                       helpIcon,
                                       value,
                                       onChange,
                                       placeholder = "",
                                       disabled = false,
                                       error,
                                   }) => (
    <div className="cmr-field-group">
        <CmrFieldLabel required={required} tooltip={tooltip}>
            {label}
        </CmrFieldLabel>

        <InputText
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full"
        />

        <CmrFieldError message={error} />
        {helpText && <CmrHelpText icon={helpIcon}>{helpText}</CmrHelpText>}
    </div>
));
CmrInputField.displayName = "CmrInputField";

// ─── ORGANISMO: CmrStepsBar ───────────────────────────────────────────────────

const STEPS = [
    { num: 1, label: "Selección de ruta"     },
    { num: 2, label: "Modificar estructuras" },
    { num: 3, label: "Confirmar"             },
];

export const CmrStepsBar = memo(({ current }) => (
    <nav className="cmr-steps-bar p-3" aria-label="Progreso del formulario">
        <div className="cmr-steps-bar__track flex align-items-center">
            {STEPS.map((s, i) => {
                const active = current === s.num;
                const done   = current > s.num;
                return (
                    <div key={s.num} className={`cmr-step-cell${i < STEPS.length - 1 ? " flex-1" : ""}`}>
                        <div className="cmr-step-circle-wrap">
                            <div
                                className={`cmr-step__circle flex align-items-center justify-content-center
                                    ${active ? "cmr-step__circle--active" : done ? "cmr-step__circle--done" : ""}`}
                                aria-current={active ? "step" : undefined}
                            >
                                {done ? <i className="pi pi-check" aria-hidden="true" /> : s.num}
                            </div>
                            <span className={`cmr-step__label${active ? " cmr-step__label--active" : done ? " cmr-step__label--done" : ""}`}>
                                {s.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`cmr-step-connector${done ? " cmr-step-connector--done" : ""}`} aria-hidden="true" />
                        )}
                    </div>
                );
            })}
        </div>
    </nav>
));
CmrStepsBar.displayName = "CmrStepsBar";

/** Normaliza "HH:mm:ss" → "HH:mm" para comparar y mostrar horas */
export const hhmm = (h) => (h ? String(h).slice(0, 5) : h);